import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, Box, Typography, Switch, FormControlLabel, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { ProductInput, updateProduct, createProduct, fetchCategories } from "../data/api";
import { toCategoryVM } from "../data/mappers";
import ImageInput from "../components/ImageInput";

interface Props {
  open: boolean;
  /** Raw backend product (null = creating a new one). */
  product: any | null;
  onClose: () => void;
  onSaved: (saved?: any) => void;
}

const blank: ProductInput = { name: "", price: 0, stock: 0, available: true };

const ProductEditDialog: React.FC<Props> = ({ open, product, onClose, onSaved }) => {
  const [form, setForm] = useState<ProductInput>(blank);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const cats = (await fetchCategories() || []).map(toCategoryVM).map((c) => c.name).filter(Boolean);
      setCategories(Array.from(new Set(cats)));
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setErr("");
    if (product) {
      setForm({
        id: product.id,
        name: product.name ?? "",
        description: product.description ?? "",
        price: Number(product.price ?? 0),
        stock: Number(product.stock ?? 0),
        category: product.category ?? "",
        vendor: product.vendor ?? "",
        image: product.image ?? "",
        sku: product.sku ?? "",
        packagingUnit: product.packagingUnit ?? "",
        unitsPerCase: product.unitsPerCase != null ? Number(product.unitsPerCase) : undefined,
        available: product.available ?? true,
      });
    } else {
      setForm(blank);
    }
  }, [open, product]);

  const set = (k: keyof ProductInput, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { setErr("Name is required."); return; }
    setSaving(true);
    try {
      const saved = product ? await updateProduct(form) : await createProduct(form);
      // Guarantee the chosen image shows immediately even if the API response omits it.
      onSaved({ ...form, ...(saved || {}), image: (saved && saved.image) ? saved.image : form.image });
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.message || "Save failed. Is the backend reachable?");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{product ? `Edit ${product.name}` : "New product"}</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Stack spacing={2}>
          <ImageInput value={form.image || ""} onChange={(v) => set("image", v)} label="Product image" />

          <TextField label="Name" value={form.name} required onChange={(e) => set("name", e.target.value)} fullWidth />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="SKU" value={form.sku ?? ""} onChange={(e) => set("sku", e.target.value)} fullWidth />
            <FormControl fullWidth>
              <InputLabel id="cat-label">Category</InputLabel>
              <Select
                labelId="cat-label" label="Category"
                value={form.category ?? ""}
                onChange={(e) => set("category", e.target.value)}
              >
                <MenuItem value=""><em>— Select a category —</em></MenuItem>
                {form.category && !categories.includes(form.category) && (
                  <MenuItem value={form.category}>{form.category} (current)</MenuItem>
                )}
                {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <TextField label="Description" value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} fullWidth multiline minRows={2} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Packaging unit" value={form.packagingUnit ?? ""} onChange={(e) => set("packagingUnit", e.target.value)} fullWidth placeholder="Case (25 lb)" />
            <TextField label="Units per case" type="number" value={form.unitsPerCase ?? ""} onChange={(e) => set("unitsPerCase", e.target.value === "" ? undefined : Number(e.target.value))} fullWidth />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Price (CAD / case)" type="number" value={form.price} onChange={(e) => set("price", Number(e.target.value))} fullWidth />
            <TextField label="Stock (cases)" type="number" value={form.stock} onChange={(e) => set("stock", Number(e.target.value))} fullWidth />
          </Stack>
          <FormControlLabel control={<Switch checked={!!form.available} onChange={(e) => set("available", e.target.checked)} />} label="Available in catalogue" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductEditDialog;
